import { type BedDto, type GuestDto, type RoomDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { BedIllustration } from '../../shared/illustrations/BedIllustration.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Badge,
  Button,
  Card,
  ConfirmButton,
  ErrorAlert,
  LoadingBlock,
} from '../../shared/ui/index.js';
import { BedDialog } from './BedDialog.js';
import { useBeds, useDeleteBed, useDeleteRoom } from './houseHooks.js';

export interface RoomCardProps {
  readonly houseId: string;
  readonly room: RoomDto;
  readonly guests: readonly GuestDto[];
  readonly onEdit: () => void;
}

export function RoomCard({ houseId, room, guests, onEdit }: RoomCardProps): ReactElement {
  const t = useTranslation();
  const beds = useBeds(houseId, room.id);
  const deleteRoom = useDeleteRoom(houseId);
  const deleteBed = useDeleteBed(houseId, room.id);
  const [addingBed, setAddingBed] = useState(false);
  const [editingBed, setEditingBed] = useState<BedDto | null>(null);

  const overCapacity = room.sleepingPlaces > room.maxCapacity;

  return (
    <Card>
      <div className="stack">
        <div className="row row--between">
          <div className="stack stack--tight">
            <h3 className="card__title">
              {room.name}
              {overCapacity ? (
                <Badge tone="warning">
                  {t.roomCard.placesCapacity(room.sleepingPlaces, room.maxCapacity)}
                </Badge>
              ) : null}
            </h3>
            <p className="card__meta">
              {t.roomCard.holdsUpTo(t.count.people(room.maxCapacity))} ·{' '}
              {t.count.beds(room.bedCount)}
            </p>
          </div>
          <div className="row">
            <Button small variant="secondary" onClick={onEdit}>
              {t.roomCard.editRoom}
            </Button>
            <ConfirmButton
              small
              label={t.common.delete}
              onConfirm={() => deleteRoom.mutate(room.id)}
            />
          </div>
        </div>

        <ErrorAlert error={beds.error ?? deleteRoom.error ?? deleteBed.error} />

        {beds.isPending ? <LoadingBlock label={t.roomCard.loadingBeds} /> : null}

        {beds.data?.length === 0 ? <p className="field__hint">{t.roomCard.noBedsYet}</p> : null}

        {beds.data !== undefined && beds.data.length > 0 ? (
          <div className="room-scene">
            <span className="room-scene__window" />
            <ul className="data-list room-scene__beds">
              {beds.data.map((bed) => (
                <li key={bed.id} className="list-row">
                  <div className="list-row__main">
                    <div className="row">
                      <BedIllustration type={bed.type} option={bed.option} />
                      <span className="list-row__title" title={bed.name}>
                        {bedKindLabel(t, bed.type, bed.option)}
                      </span>
                    </div>
                    <span className="list-row__meta">
                      {bed.sizeCm} cm
                      {bed.sizeIsCustom ? t.roomCard.custom : ''} · {t.roomCard.sleeps(bed.sleeps)}
                      {bed.favoriteGuestIds.length > 0
                        ? ` · ${t.roomCard.wants(
                            bed.favoriteGuestIds
                              .map(
                                (guestId) =>
                                  guests.find((guest) => guest.id === guestId)?.name ??
                                  t.guestsPage.someone,
                              )
                              .join(', '),
                          )}`
                        : ''}
                    </span>
                  </div>
                  <div className="row">
                    <Button small variant="secondary" onClick={() => setEditingBed(bed)}>
                      {t.common.edit}
                    </Button>
                    <ConfirmButton
                      small
                      label={t.common.delete}
                      onConfirm={() => deleteBed.mutate(bed.id)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div>
          <Button small variant="secondary" onClick={() => setAddingBed(true)}>
            {t.roomCard.addABed}
          </Button>
        </div>
      </div>

      {addingBed ? (
        <BedDialog
          houseId={houseId}
          roomId={room.id}
          guests={guests}
          onClose={() => setAddingBed(false)}
        />
      ) : null}

      {editingBed !== null ? (
        <BedDialog
          houseId={houseId}
          roomId={room.id}
          bed={editingBed}
          guests={guests}
          onClose={() => setEditingBed(null)}
        />
      ) : null}
    </Card>
  );
}
